import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  SignOut, ShieldStar, MagnifyingGlass, CalendarBlank, FileText, Receipt, Wallet, ArrowRight, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABELS = {
  schaden_gemeldet: { label: "Schaden gemeldet", color: "bg-slate-100 text-slate-700" },
  unterlagen_geprueft: { label: "Unterlagen geprüft", color: "bg-blue-50 text-blue-700" },
  versicherung_informiert: { label: "Versicherung informiert", color: "bg-indigo-50 text-indigo-700" },
  termin_bestaetigt: { label: "Termin bestätigt", color: "bg-amber-50 text-amber-700" },
  fahrzeug_in_bearbeitung: { label: "In Bearbeitung", color: "bg-purple-50 text-purple-700" },
  fertig_zur_abholung: { label: "Fertig", color: "bg-emerald-50 text-emerald-700" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login");
      return;
    }
    const load = async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/claims"),
        ]);
        setStats(s.data);
        setClaims(c.data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("admin_token");
          navigate("/admin/login");
        } else {
          toast.error("Daten konnten nicht geladen werden");
        }
      }
    };
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const filtered = claims.filter((c) => {
    const matchSearch =
      !search ||
      (c.tracking_code || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.license_plate || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.insurance_company || "").toLowerCase().includes(search.toLowerCase());
    const matchTab =
      tab === "all" ||
      (tab === "insurance" && c.flow_type === "insurance") ||
      (tab === "self_pay" && c.flow_type === "self_pay") ||
      (tab === "today" && c.appointment_date === new Date().toISOString().slice(0, 10));
    return matchSearch && matchTab;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-slate-900 text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <ShieldStar size={20} weight="fill" color="white" />
            </div>
            <div>
              <div className="font-bold">Autoglas Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300 -mt-0.5">Control Room</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button onClick={() => navigate("/admin/toenung")} variant="ghost" className="text-pink-300 hover:text-white hover:bg-slate-800" data-testid="admin-tint-link">
              Scheibentönung
            </Button>
            <Button onClick={() => navigate("/admin/sattlerei")} variant="ghost" className="text-[#7fdc92] hover:text-white hover:bg-slate-800" data-testid="admin-sattlerei-link">
              Sattlerei
            </Button>
            <Button onClick={() => navigate("/admin/emails")} variant="ghost" className="text-sky-300 hover:text-white hover:bg-slate-800" data-testid="admin-emails-link">
              E-Mails
            </Button>
          </div>
          <Button onClick={logout} variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="admin-logout">
            <SignOut size={18} className="mr-1.5" /> Abmelden
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard icon={Plus} label="Neue Schäden heute" value={stats?.new_today} color="bg-blue-600" testid="stat-new-today" />
          <StatCard icon={CalendarBlank} label="Termine heute" value={stats?.appointments_today} color="bg-amber-500" testid="stat-appointments-today" />
          <StatCard icon={Receipt} label="Offene Versicherungen" value={stats?.open_insurance} color="bg-indigo-500" testid="stat-open-insurance" />
          <StatCard icon={Wallet} label="Selbstzahler offen" value={stats?.self_pay_pending} color="bg-purple-500" testid="stat-self-pay-pending" />
          <StatCard icon={FileText} label="Schäden gesamt" value={stats?.total_claims} color="bg-slate-900" testid="stat-total" />
        </div>

        {/* Filter row */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div className="flex flex-col md:flex-row items-center gap-3 justify-between">
            <h2 className="text-xl font-bold text-slate-900 mr-auto">Kundenliste</h2>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Suche Kennzeichen, Name, Code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-11 pl-10"
                  data-testid="admin-search"
                />
              </div>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all" data-testid="tab-all">Alle ({claims.length})</TabsTrigger>
              <TabsTrigger value="insurance" data-testid="tab-insurance">Versicherung</TabsTrigger>
              <TabsTrigger value="self_pay" data-testid="tab-self-pay">Selbstzahler</TabsTrigger>
              <TabsTrigger value="today" data-testid="tab-today">Termine heute</TabsTrigger>
            </TabsList>
            <TabsContent value={tab} className="pt-4">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Tracking</TableHead>
                      <TableHead>Kunde</TableHead>
                      <TableHead>Fahrzeug</TableHead>
                      <TableHead>Versicherung</TableHead>
                      <TableHead>Termin</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          Keine Schäden gefunden.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((c) => (
                        <TableRow key={c.id} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => navigate(`/admin/claims/${c.id}`)} data-testid={`row-claim-${c.id}`}>
                          <TableCell className="font-mono font-semibold text-slate-900">{c.tracking_code}</TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900">{c.customer_name || "—"}</div>
                            <div className="text-xs text-slate-500">{c.customer_phone}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900">{c.license_plate || "—"}</div>
                            <div className="text-xs text-slate-500">{c.manufacturer} {c.model}</div>
                          </TableCell>
                          <TableCell>{c.insurance_company || (c.flow_type === "self_pay" ? "—" : "—")}</TableCell>
                          <TableCell>
                            {c.appointment_date ? (
                              <div>
                                <div className="text-sm">{new Date(c.appointment_date).toLocaleDateString("de-DE")}</div>
                                <div className="text-xs text-slate-500">{c.appointment_time}</div>
                              </div>
                            ) : "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={c.flow_type === "self_pay" ? "outline" : "secondary"}>
                              {c.flow_type === "self_pay" ? "Selbstzahler" : "Versicherung"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_LABELS[c.status]?.color}`}>
                              {STATUS_LABELS[c.status]?.label || c.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="text-blue-600">
                              <ArrowRight size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}>
      <Icon size={20} color="white" weight="duotone" />
    </div>
    <div className="text-3xl font-bold text-slate-900">{value ?? "—"}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);
